import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { addDays, addMonths, format, getDay, isSameMonth, startOfMonth, subMonths } from "date-fns";
import { useDispatch } from "react-redux";
import { getCspmChecks } from "../../../store/cspm/cspmSlice";
import { formatUtcDateSimple } from "../../../helpers/formatUtcDate";
import { ACCOUNT_DETAIL_FONT_SX } from "../../../components/AccountDetailPolicy/accountDetailTypography";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const ACCENT = "#F98125";

const CLOSE_X_STYLE = {
  padding: 0,
  margin: 0,
  border: "none",
  background: "transparent",
  color: "#FFFFFF",
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "none",
  flexShrink: 0,
  WebkitTextStroke: "0.6px #FFFFFF",
};

const GREY_CLOSE_BTN_SX = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  color: "#fff",
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#5a6268",
    borderColor: "#5a6268",
    boxShadow: "none",
  },
};

const pad = (n) => String(n).padStart(2, "0");

const toDayKey = (dateObj) => {
  if (!dateObj || Number.isNaN(dateObj.getTime())) return "";
  return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;
};

const apiDayKey = (value) => String(value || "").replace("T", " ").slice(0, 10);

const dateFromApiDayKey = (dayKey) => {
  if (!dayKey || dayKey.length < 10) return new Date();
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

const formatDayHeading = (dayKey) => {
  if (!dayKey) return "this date";
  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) return dayKey;
  return format(new Date(year, month - 1, day), "EEEE, MMM d, yyyy");
};

const toTitleCase = (str) => {
  if (!str) return "—";
  return String(str)
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatStatusText = (status) => {
  if (!status) return "—";
  return String(status)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const isCompleteStatus = (status) =>
  (status || "").toLowerCase().replace(/_/g, " ").trim() === "complete";

const formatUtcDateString = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const [datePart, timePart] = dateStr.includes("T") ? dateStr.split("T") : dateStr.split(" ");
    if (!datePart || !timePart) return "N/A";
    const [year, month, day] = datePart.split("-");
    const [hours, minutes, seconds] = timePart.split(":");
    if (!year || !month || !day) return "N/A";
    const date = new Date(
      Date.UTC(
        parseInt(year, 10),
        parseInt(month, 10) - 1,
        parseInt(day, 10),
        parseInt(hours, 10) || 0,
        parseInt(minutes, 10) || 0,
        parseInt(seconds, 10) || 0
      )
    );
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${dayNames[date.getUTCDay()]} ${monthNames[parseInt(month, 10) - 1]} ${parseInt(day, 10)} ${year} ${hours || "00"}:${minutes || "00"}:${seconds || "00"} UTC`;
  } catch (error) {
    return "N/A";
  }
};

const statusChipSx = (status) => {
  const value = (status || "").toLowerCase().replace(/_/g, " ").trim();
  if (value === "complete") return { bgcolor: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" };
  if (value === "failed") return { bgcolor: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" };
  if (value.includes("progress") || value.includes("provisioning") || value.includes("generating")) {
    return { bgcolor: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" };
  }
  return { bgcolor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" };
};

const ScanMonthCalendar = ({
  visibleMonth,
  onMonthChange,
  selectedDayKey,
  scanDayKeys,
  onSelectDay,
  minMonth,
  maxMonth,
}) => {
  const monthStart = startOfMonth(visibleMonth);
  const firstCell = addDays(monthStart, -getDay(monthStart));
  const cells = Array.from({ length: 42 }, (_, index) => addDays(firstCell, index));
  const canPrev = !minMonth || monthStart > startOfMonth(minMonth);
  const canNext = !maxMonth || monthStart < startOfMonth(maxMonth);

  return (
    <Box
      sx={{
        p: 2,
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        bgcolor: "#fff",
        height: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <IconButton
          size="small"
          onClick={() => canPrev && onMonthChange(subMonths(visibleMonth, 1))}
          disabled={!canPrev}
          aria-label="Previous month"
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.95rem" }}>
          {format(visibleMonth, "MMMM yyyy")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => canNext && onMonthChange(addMonths(visibleMonth, 1))}
          disabled={!canNext}
          aria-label="Next month"
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {WEEKDAYS.map((day) => (
          <Typography
            key={day}
            align="center"
            sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", py: 0.5 }}
          >
            {day}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.25 }}>
        {cells.map((day) => {
          const key = toDayKey(day);
          const inMonth = isSameMonth(day, visibleMonth);
          const hasScan = scanDayKeys.has(key);
          const selected = selectedDayKey === key;
          return (
            <Box
              key={`${key}-${inMonth ? "in" : "out"}`}
              component="button"
              type="button"
              disabled={!hasScan}
              aria-pressed={selected}
              aria-label={`${format(day, "MMMM d, yyyy")}${hasScan ? ", has scans" : ""}`}
              onClick={() => hasScan && onSelectDay(day)}
              sx={{
                border: 0,
                bgcolor: selected ? ACCENT : "transparent",
                color: selected ? "#fff" : !inMonth ? "#cbd5e1" : hasScan ? "#111827" : "#94a3b8",
                borderRadius: "8px",
                height: 36,
                cursor: hasScan ? "pointer" : "default",
                fontWeight: selected || hasScan ? 700 : 500,
                fontSize: "0.8rem",
                position: "relative",
                "&:hover": hasScan && !selected ? { bgcolor: "#fff7ed" } : {},
                "&:disabled": { cursor: "default" },
              }}
            >
              {day.getDate()}
              {hasScan && !selected && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: ACCENT,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <Typography sx={{ mt: 1.5, fontSize: "0.72rem", color: "#64748b", lineHeight: 1.4 }}>
        Orange dots mark days with scans. Only those days can be opened.
      </Typography>
    </Box>
  );
};

const ListScans = ({
  open,
  onClose,
  scans = [],
  selectedAccount,
  onScanSelect,
  availableDates = [],
  complianceResultOrderedMap = {},
  nextRunDate,
  initialScanDate,
  workloadScheduleName,
  actionLabel = "View",
  heading,
  showNextRun = true,
  closeOnSelect = true,
  isLoading = false,
  excludeRunId,
}) => {
  const dispatch = useDispatch();
  const isSelectMode = actionLabel !== "View";

  const recurringNextRunDate = useMemo(() => {
    const now = new Date();
    const allRecurringNextDates = [];

    (scans || []).forEach((scan) => {
      if (scan?.scheduleType === "recurring" && scan?.nextRunDate) {
        allRecurringNextDates.push(scan.nextRunDate);
      }
    });

    if (complianceResultOrderedMap && typeof complianceResultOrderedMap === "object") {
      Object.values(complianceResultOrderedMap).forEach((scan) => {
        if (
          scan?.scheduleType === "recurring" &&
          scan?.nextRunDate &&
          !allRecurringNextDates.includes(scan.nextRunDate)
        ) {
          allRecurringNextDates.push(scan.nextRunDate);
        }
      });
    }

    if (!allRecurringNextDates.length) return null;

    const futureDatesWithStrings = allRecurringNextDates
      .map((dateStr) => {
        const [datePart, timePart] = dateStr.includes("T") ? dateStr.split("T") : dateStr.split(" ");
        if (!datePart || !timePart) return null;
        const [year, month, day] = datePart.split("-");
        const [hours, minutes, seconds] = timePart.split(":");
        const date = new Date(
          Date.UTC(
            parseInt(year, 10),
            parseInt(month, 10) - 1,
            parseInt(day, 10),
            parseInt(hours, 10) || 0,
            parseInt(minutes, 10) || 0,
            parseInt(seconds, 10) || 0
          )
        );
        return { date, originalString: dateStr };
      })
      .filter((item) => item && !Number.isNaN(item.date.getTime()) && item.date > now);

    if (!futureDatesWithStrings.length) return null;
    futureDatesWithStrings.sort((a, b) => a.date - b.date);
    return futureDatesWithStrings[0].originalString;
  }, [scans, complianceResultOrderedMap]);

  const displayNextRunDate = recurringNextRunDate || nextRunDate;

  const getPreferredDefaultDate = () => {
    if (initialScanDate) return dateFromApiDayKey(apiDayKey(initialScanDate));
    const scanDateValues = (scans || [])
      .map((scan) => scan?.scanDate)
      .filter(Boolean)
      .sort((a, b) => String(b).localeCompare(String(a)));
    if (scanDateValues.length) return dateFromApiDayKey(apiDayKey(scanDateValues[0]));
    const datesWithScans = (availableDates || []).filter((dt) =>
      (scans || []).some((scan) => apiDayKey(scan?.scanDate) === apiDayKey(dt))
    );
    const pool = datesWithScans.length ? datesWithScans : availableDates || [];
    if (pool.length) {
      const latest = [...pool].sort((a, b) => String(b).localeCompare(String(a)))[0];
      return dateFromApiDayKey(apiDayKey(latest));
    }
    return new Date();
  };

  const [selectedDate, setSelectedDate] = useState(getPreferredDefaultDate);
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(getPreferredDefaultDate()));
  const [pendingScanId, setPendingScanId] = useState(null);

  const scanDayKeys = useMemo(
    () => new Set((availableDates || []).map((value) => apiDayKey(value))),
    [availableDates]
  );

  const monthBounds = useMemo(() => {
    if (!availableDates.length) return { min: null, max: null };
    const dates = availableDates.map((value) => dateFromApiDayKey(apiDayKey(value)));
    return {
      min: new Date(Math.min(...dates)),
      max: new Date(Math.max(...dates)),
    };
  }, [availableDates]);

  const fetchDay = (dateObj) => {
    if (!selectedAccount || !dateObj) return;
    const dayKey = toDayKey(dateObj);
    const exactDateTime =
      (availableDates || []).find((dt) => String(dt).startsWith(dayKey)) ||
      (scans || []).find((scan) => apiDayKey(scan?.scanDate) === dayKey)?.scanDate ||
      (initialScanDate && apiDayKey(initialScanDate) === dayKey ? initialScanDate : null);
    if (!exactDateTime) return;
    dispatch(
      getCspmChecks({
        accountNumber: selectedAccount,
        date: exactDateTime,
        ...(workloadScheduleName ? { workloadScheduleName } : {}),
      })
    );
  };

  useEffect(() => {
    if (!open) return;
    const nextDate = getPreferredDefaultDate();
    setSelectedDate(nextDate);
    setVisibleMonth(startOfMonth(nextDate));
    setPendingScanId(null);
    fetchDay(nextDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedAccount]);

  const selectedDayKey = toDayKey(selectedDate);

  const dayScans = useMemo(
    () =>
      (scans || [])
        .filter((scan) => scan?.scanDate && apiDayKey(scan.scanDate) === selectedDayKey)
        .sort(
          (a, b) =>
            new Date(String(b.scanDate).replace(" ", "T")) -
            new Date(String(a.scanDate).replace(" ", "T"))
        ),
    [scans, selectedDayKey]
  );

  const handleSelectDay = (day) => {
    setSelectedDate(day);
    setPendingScanId(null);
    fetchDay(day);
  };

  const selectScan = (scan) => {
    if (!scan) return;
    if (!isCompleteStatus(scan.runStatus)) return;
    if (excludeRunId && scan.runId === excludeRunId) return;
    // Only this path updates the parent scan field / rendered scan data.
    onScanSelect(scan);
    if (closeOnSelect) onClose();
  };

  const handlePrimary = () => {
    const scan = dayScans.find((item) => item.runId === pendingScanId);
    if (!scan) return;
    selectScan(scan);
  };

  const canViewPending = Boolean(
    pendingScanId &&
      dayScans.some(
        (scan) =>
          scan.runId === pendingScanId &&
          isCompleteStatus(scan.runStatus) &&
          !(excludeRunId && scan.runId === excludeRunId)
      )
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="scans-modal"
      aria-describedby="modal-for-viewing-scans"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "none",
          border: "1px solid #e2e8f0",
          ...ACCOUNT_DETAIL_FONT_SX,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          backgroundColor: ACCENT,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            id="scans-modal"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.125rem", sm: "1.25rem" },
              color: "#FFFFFF",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            {heading || "Select a scan"}
          </Typography>
          <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
            Account {selectedAccount || "—"}. Browse by date, then {isSelectMode ? "select" : "view"} a
            completed scan.
          </Typography>
        </Box>
        <button type="button" aria-label="Close" onClick={onClose} style={CLOSE_X_STYLE}>
          <span aria-hidden="true">×</span>
        </button>
      </Box>

      <DialogContent
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          backgroundColor: "#f3f4f6",
          flex: 1,
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(260px, 0.95fr) minmax(0, 1.25fr)" },
            gap: 1.5,
            alignItems: "stretch",
            minHeight: 320,
          }}
        >
          <ScanMonthCalendar
            visibleMonth={visibleMonth}
            onMonthChange={setVisibleMonth}
            selectedDayKey={selectedDayKey}
            scanDayKeys={scanDayKeys}
            onSelectDay={handleSelectDay}
            minMonth={monthBounds.min}
            maxMonth={monthBounds.max}
          />

          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              bgcolor: "#fff",
              p: 2,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              position: "relative",
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.875rem", mb: 1.25 }}>
              Scans · {formatDayHeading(selectedDayKey)}
            </Typography>

            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.72)",
                  zIndex: 2,
                  borderRadius: "12px",
                }}
              >
                <CircularProgress size={32} sx={{ color: ACCENT }} />
              </Box>
            )}

            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              {!dayScans.length ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 180,
                    color: "#94a3b8",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  No scans for this date.
                </Box>
              ) : (
                dayScans.map((scan) => {
                  const selected = pendingScanId === scan.runId;
                  const complete = isCompleteStatus(scan.runStatus);
                  const excluded = Boolean(excludeRunId && scan.runId === excludeRunId);
                  const disabled = !complete || excluded;
                  const chip = statusChipSx(scan.runStatus);
                  return (
                    <Box
                      key={scan.runId || scan.scanDate}
                      component="button"
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setPendingScanId(scan.runId);
                      }}
                      onDoubleClick={() => {
                        if (disabled) return;
                        selectScan(scan);
                      }}
                      sx={{
                        display: "flex",
                        width: "100%",
                        textAlign: "left",
                        gap: 1.25,
                        mb: 1,
                        p: 1.5,
                        borderRadius: "10px",
                        border: selected ? `2px solid ${ACCENT}` : "1px solid #e2e8f0",
                        bgcolor: selected ? "#fff7ed" : "#fff",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.55 : 1,
                        "&:hover": disabled ? {} : { borderColor: ACCENT },
                      }}
                    >
                      <Box sx={{ color: selected ? ACCENT : "#94a3b8", mt: "2px" }}>
                        {selected ? (
                          <CheckCircleIcon fontSize="small" />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" />
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.875rem" }}>
                            {formatUtcDateSimple(scan.scanDate)}
                          </Typography>
                          {selected && (
                            <Chip
                              size="small"
                              label="Selected"
                              sx={{ height: 22, fontWeight: 700, bgcolor: "#ffedd5", color: "#9a3412" }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                          <Chip
                            size="small"
                            label={toTitleCase(scan.runType)}
                            sx={{ height: 22, fontWeight: 600 }}
                          />
                          <Chip
                            size="small"
                            label={toTitleCase(scan.scheduleType)}
                            sx={{ height: 22, fontWeight: 600 }}
                          />
                          <Chip
                            size="small"
                            label={
                              scan.resourceScopeType === "Full Run"
                                ? "Full Scan"
                                : scan.resourceScopeType || "—"
                            }
                            sx={{ height: 22, fontWeight: 600 }}
                          />
                          <Chip
                            size="small"
                            label={formatStatusText(scan.runStatus)}
                            sx={{ height: 22, fontWeight: 700, ...chip }}
                          />
                          {excluded && (
                            <Chip size="small" label="Already selected" sx={{ height: 22, fontWeight: 600 }} />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: "1px solid #e2e8f0",
          bgcolor: "#fff",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button variant="contained" disableElevation onClick={onClose} sx={GREY_CLOSE_BTN_SX}>
          Close
        </Button>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: 0,
            px: 1,
          }}
        >
          {showNextRun && displayNextRunDate ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                maxWidth: "100%",
                px: 1.75,
                py: 0.85,
                borderRadius: "10px",
                bgcolor: "#e3f2fd",
                border: "1px solid #90caf9",
              }}
            >
              <ScheduleIcon sx={{ fontSize: 20, color: "#1976d2", flexShrink: 0 }} />
              <Typography
                sx={{
                  m: 0,
                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                  fontWeight: 600,
                  color: "#0d47a1",
                  lineHeight: 1.3,
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                }}
              >
                Next scan:{" "}
                <Box component="span" sx={{ fontWeight: 800, color: "#1565c0" }}>
                  {formatUtcDateString(displayNextRunDate)}
                </Box>
              </Typography>
            </Box>
          ) : null}
        </Box>
        <Button
          variant="contained"
          disableElevation
          onClick={handlePrimary}
          disabled={!canViewPending}
          startIcon={isSelectMode ? <CheckIcon /> : <VisibilityIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: "8px",
            px: 2.5,
            backgroundColor: ACCENT,
            boxShadow: "none",
            "&:hover": { backgroundColor: "#e06e14", boxShadow: "none" },
            "&.Mui-disabled": { backgroundColor: "#fed7aa", color: "#fff" },
          }}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListScans;
